<template>
  <!-- Fullscreen mobile: ion-modal for iOS card presentation -->
  <ion-modal
    v-if="useIonicModal"
    :is-open="modelValue"
    :class="modalClass"
    :presenting-element="presentingElement"
    @did-dismiss="close">
    <div
      ref="sheetRef"
      class="flex flex-col h-full bg-gray-900 rounded-t-2xl pb-[env(safe-area-inset-bottom,0px)]">
      <!-- Drag handle -->
      <div
        class="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd">
        <div class="w-10 h-1 rounded-full bg-white/30" />
      </div>

      <!-- Header -->
      <div
        class="flex justify-between items-center px-4 pt-1 pb-2"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd">
        <div class="flex-1 min-w-0">
          <slot name="header">
            <span v-if="title" class="text-lg font-semibold text-white">
              {{ title }}
            </span>
          </slot>
        </div>
        <button
          type="button"
          class="flex justify-center items-center rounded-full transition-all duration-150 cursor-pointer size-8 bg-white/10 text-white/80 hover:bg-white/20 active:scale-95"
          @click.stop="close">
          <fa-icon :icon="closeIcon" />
        </button>
      </div>

      <!-- Content -->
      <div
        ref="contentRef"
        :class="[
          'flex-1 min-h-0',
          tall ? 'overflow-hidden' : 'overflow-y-auto',
          contentClass
        ]"
        :style="keyboardStyle()"
        @touchstart="onContentTouchStart"
        @touchmove="onContentTouchMove"
        @touchend="onContentTouchEnd">
        <slot />
      </div>

      <!-- Footer -->
      <div v-if="$slots.footer" class="px-4 pb-4 pt-2 border-t border-white/10">
        <slot name="footer" />
      </div>
    </div>
  </ion-modal>

  <!-- All other cases: Teleport overlay with proper backdrop -->
  <Teleport v-else to="body">
    <Transition :name="isMobileApp ? 'sheet-mobile' : 'sheet-desktop'">
      <div
        v-if="modelValue"
        class="sheet-overlay"
        :class="isMobileApp ? 'items-end' : 'items-center'"
        :style="overlayStyle"
        @click.self="close">
        <div
          ref="sheetRef"
          :class="[
            isMobileApp ? mobileContainerClass : desktopContainerClass,
            isMobileApp ? 'sheet-container-mobile' : 'sheet-container-desktop',
            sizeClass
          ]">
            <!-- Mobile: Drag handle + header -->
            <template v-if="isMobileApp">
              <div
                class="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
                @touchstart="onTouchStart"
                @touchmove="onTouchMove"
                @touchend="onTouchEnd">
                <div class="w-10 h-1 rounded-full bg-white/30" />
              </div>
              <div
                class="flex justify-between items-center px-4 pt-1 pb-2"
                @touchstart="onTouchStart"
                @touchmove="onTouchMove"
                @touchend="onTouchEnd">
                <div class="flex-1 min-w-0">
                  <slot name="header">
                    <span v-if="title" class="text-lg font-semibold text-white">
                      {{ title }}
                    </span>
                  </slot>
                </div>
                <button
                  type="button"
                  class="flex justify-center items-center rounded-full transition-all duration-150 cursor-pointer size-8 bg-white/10 text-white/80 hover:bg-white/20 active:scale-95"
                  @click.stop="close">
                  <fa-icon :icon="closeIcon" />
                </button>
              </div>
            </template>

            <!-- Desktop: Header -->
            <template v-else>
              <div class="flex justify-between items-center px-6 pt-5 pb-3">
                <div class="flex-1 min-w-0">
                  <slot name="header">
                    <span v-if="title" class="text-lg font-semibold text-white">
                      {{ title }}
                    </span>
                  </slot>
                </div>
                <button
                  type="button"
                  class="flex justify-center items-center rounded-full transition-all duration-150 cursor-pointer size-8 bg-white/10 text-white/80 hover:bg-white/20 active:scale-95"
                  @click="close">
                  <fa-icon :icon="closeIcon" />
                </button>
              </div>
            </template>

            <!-- Content -->
            <div
              ref="contentRef"
              :class="[
                'flex-1 min-h-0',
                tall ? 'overflow-hidden' : 'overflow-y-auto',
                isMobileApp && !fullscreen && !tall ? 'max-h-[60vh]' : '',
                contentClass
              ]"
              :style="keyboardStyle()"
              @touchstart="onContentTouchStart"
              @touchmove="onContentTouchMove"
              @touchend="onContentTouchEnd">
              <slot />
            </div>

            <!-- Footer -->
            <div
              v-if="$slots.footer"
              :class="[
                'border-t border-white/10',
                isMobileApp ? 'px-4 pb-4 pt-2' : 'px-6 pb-6 pt-2'
              ]">
              <slot name="footer" />
            </div>
          </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { inject, ref, computed, watch, onUnmounted } from 'vue'
import { useKeyboardAvoidance } from '@/composables/useKeyboardAvoidance'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v)
  },
  contentClass: {
    type: String,
    default: 'px-6 pb-6'
  },
  closeIcon: {
    type: Array,
    default: () => ['fas', 'xmark']
  },
  fullscreen: {
    type: Boolean,
    default: false
  },
  tall: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'close'])

const { isMobileApp, isIOS } = inject('platform', {
  isMobileApp: false,
  isIOS: ref(false)
})

const { contentStyle: keyboardStyle } = useKeyboardAvoidance()

// Only use ion-modal for fullscreen mobile (iOS card presentation)
const useIonicModal = computed(() => {
  const isMobile = typeof isMobileApp === 'object' && 'value' in isMobileApp
    ? isMobileApp.value
    : isMobileApp
  return isMobile && props.fullscreen
})

// Presenting element for iOS card-style fullscreen modals
const presentingElement = ref(null)

// Check if we're inside a card stack context (for z-index layering)
const inCardStackContext = inject('inCardStackContext', false)

// Compute modal class for ion-modal (fullscreen only)
const modalClass = computed(() => {
  const classes = ['sheet-modal-fullscreen']
  if (inCardStackContext) {
    classes.push('spectra-modal-card-context')
  }
  const isMobile = typeof isMobileApp === 'object' && 'value' in isMobileApp
    ? isMobileApp.value
    : isMobileApp
  if (isMobile) {
    classes.push('sheet-modal-fullscreen-mobile')
  }
  return classes.join(' ')
})

// Set up presenting element for iOS card-style presentation when fullscreen
watch(
  () => props.modelValue,
  async (isOpen) => {
    if (isOpen && useIonicModal.value) {
      const isIOSVal = typeof isIOS === 'object' && 'value' in isIOS ? isIOS.value : isIOS
      if (isIOSVal) {
        presentingElement.value = document.querySelector('ion-router-outlet')
      }
    } else if (!isOpen && presentingElement.value) {
      setTimeout(() => {
        presentingElement.value = null
      }, 350)
    }
  }
)

// Overlay z-index style (higher when in card stack context)
const overlayStyle = computed(() => {
  return { zIndex: inCardStackContext ? 60000 : 100 }
})

// Container classes
const mobileContainerClass = computed(() => {
  return [
    'w-full flex flex-col bg-gray-900 rounded-t-2xl pb-[env(safe-area-inset-bottom,0px)]',
    'shadow-[0_-10px_40px_-12px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.1)]',
    (props.fullscreen || props.tall) ? 'h-full' : ''
  ].filter(Boolean).join(' ')
})

const desktopContainerClass = 'w-full mx-4 max-h-[85vh] flex flex-col overflow-hidden bg-gray-900 rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.1)]'

const sizeClass = computed(() => {
  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl'
  }
  if (props.fullscreen) return 'max-w-[900px] !max-h-[90vh]'
  if (props.tall) {
    const isMobile = typeof isMobileApp === 'object' && 'value' in isMobileApp
      ? isMobileApp.value
      : isMobileApp
    if (isMobile) return 'max-h-[90vh] h-[90vh]'
    return `${sizes[props.size] || 'max-w-lg'} !max-h-[90vh] !h-[90vh]`
  }
  return sizes[props.size]
})

const close = () => {
  emit('update:modelValue', false)
  emit('close')
}

// ESC key handling
const handleKeydown = (e) => {
  if (e.key === 'Escape' && props.modelValue) {
    close()
  }
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeydown)
    } else {
      document.removeEventListener('keydown', handleKeydown)
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// Swipe-to-dismiss handling
const sheetRef = ref(null)
const contentRef = ref(null)
const touchStartY = ref(0)
const currentTranslateY = ref(0)
const isDragging = ref(false)
const isContentDragging = ref(false)

const onTouchStart = (e) => {
  touchStartY.value = e.touches[0].clientY
  currentTranslateY.value = 0
  isDragging.value = true
  if (sheetRef.value) {
    sheetRef.value.style.transition = 'none'
  }
}

const onTouchMove = (e) => {
  if (!isDragging.value) return

  const deltaY = e.touches[0].clientY - touchStartY.value

  if (deltaY > 0) {
    currentTranslateY.value = deltaY
    if (sheetRef.value) {
      sheetRef.value.style.transform = `translateY(${deltaY}px)`
    }
    e.preventDefault()
  }
}

const onTouchEnd = () => {
  if (!isDragging.value && !isContentDragging.value) return
  isDragging.value = false
  isContentDragging.value = false

  if (sheetRef.value) {
    sheetRef.value.style.transition = 'transform 200ms ease-out'

    const threshold = Math.min(100, sheetRef.value.offsetHeight * 0.3)

    if (currentTranslateY.value > threshold) {
      sheetRef.value.style.transform = `translateY(100%)`
      setTimeout(close, 200)
    } else {
      sheetRef.value.style.transform = 'translateY(0)'
    }
  }

  currentTranslateY.value = 0
}

const onContentTouchStart = (e) => {
  const content = contentRef.value
  if (content && content.scrollTop <= 0) {
    touchStartY.value = e.touches[0].clientY
    currentTranslateY.value = 0
    isContentDragging.value = false
    if (sheetRef.value) {
      sheetRef.value.style.transition = 'none'
    }
  }
}

const onContentTouchMove = (e) => {
  const content = contentRef.value
  if (!content) return

  const deltaY = e.touches[0].clientY - touchStartY.value

  if (content.scrollTop <= 0 && deltaY > 0) {
    isContentDragging.value = true
    currentTranslateY.value = deltaY
    if (sheetRef.value) {
      sheetRef.value.style.transform = `translateY(${deltaY}px)`
    }
    e.preventDefault()
  }
}

const onContentTouchEnd = () => {
  if (isContentDragging.value) {
    onTouchEnd()
  }
}
</script>

<style>
/* Teleport overlay - works on all platforms */
.sheet-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Mobile container has its own transform transition */
.sheet-container-mobile {
  transition: transform 250ms cubic-bezier(0.32, 0.72, 0, 1);
}

/* Desktop container has its own scale/opacity transition */
.sheet-container-desktop {
  transition: all 200ms ease-out;
}

/* Mobile: overlay fades, container slides up from bottom */
.sheet-mobile-enter-active,
.sheet-mobile-leave-active {
  transition: opacity 250ms ease;
}
.sheet-mobile-enter-from,
.sheet-mobile-leave-to {
  opacity: 0;
}
.sheet-mobile-enter-from .sheet-container-mobile,
.sheet-mobile-leave-to .sheet-container-mobile {
  transform: translateY(100%);
}

/* Desktop: overlay fades, container scales in */
.sheet-desktop-enter-active,
.sheet-desktop-leave-active {
  transition: opacity 200ms ease-out;
}
.sheet-desktop-enter-from,
.sheet-desktop-leave-to {
  opacity: 0;
}
.sheet-desktop-enter-from .sheet-container-desktop,
.sheet-desktop-leave-to .sheet-container-desktop {
  opacity: 0;
  transform: scale(0.95);
}

/* ion-modal styles - fullscreen only */
.sheet-modal-fullscreen {
  --width: 100%;
  --max-width: 100%;
  --height: 100%;
  --min-height: 100%;
  --max-height: 100%;
  --background: transparent;
  --border-radius: 0;
  --box-shadow: none;
}

.sheet-modal-fullscreen::part(content) {
  background: transparent;
}

.sheet-modal-fullscreen::part(backdrop) {
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* iOS card presentation - gap at top shows pushed-back content */
.sheet-modal-fullscreen.sheet-modal-fullscreen-mobile {
  --border-radius: 12px 12px 0 0;
  --height: calc(100% - 44px - env(safe-area-inset-top, 0px));
  --min-height: unset;
  --max-height: calc(100% - 44px - env(safe-area-inset-top, 0px));
}
</style>
